import { Center, Flex, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { v4 as uuid } from 'uuid';
import { SidebarContent } from 'src/UI/app-container/SidebarContent';
import { FlowHeader } from 'src/UI/generate-flow/FlowHeader';
import { PillButton } from 'src/UI/generate-flow/PillButton';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { SearchPill } from 'src/UI/generate-flow/SearchPill';
import { SiestaBarLayout } from 'src/UI/app-container/sidebar/SiestaBarLayout';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { NoteClientStep } from './steps/NoteClientStep';
import { NoteSourceStep } from './steps/NoteSourceStep';
import { NoteStructureStep } from './steps/NoteStructureStep';
import { NoteTemplateStep } from './steps/NoteTemplateStep';
import { NoteAgendaStep } from './steps/NoteAgendaStep';
import { NoteProcessingStep } from './steps/NoteProcessingStep';
import { MeetingNoteSidebar } from './components/MeetingNoteSidebar';
import { MeetingNoteFlowData, MeetingNoteStepId, MeetingNoteStepProps } from './types';

const initialData: MeetingNoteFlowData = {
  summarise: true,
};

type StepDef = {
  id: MeetingNoteStepId;
  title: string;
  disabledButtonlabel: string;
  render: (props: MeetingNoteStepProps) => React.ReactNode;
  canContinue?: (data: MeetingNoteFlowData) => boolean;
  canFilter?: boolean;
};

export default function MeetingNoteFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const backHref = location.state?.fromPath ?? `/clients/all`;

  const { flagEnabledNewSuitabilitySiestaBar, flagEnabledMeetingNoteFromAgenda } = useFlags();
  const { client: currentClient } = useCurrentClient();

  const flowId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('flowId');
    if (fromUrl) return fromUrl;
    const fromState = location.state?.flowId;
    if (fromState) return fromState;
    const id = uuid();
    const url = new URL(window.location.href);
    url.searchParams.set('flowId', id);
    window.history.replaceState(null, '', url);
    return id;
  }, [location.state?.flowId]);

  const storageKey = `meeting-note-flow-${flowId}`;

  const [data, setData] = useState<MeetingNoteFlowData>(() => {
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return initialData;
      }
    }
    return initialData;
  });

  const [stepIndex, setStepIndex] = useState(() => {
    const saved = sessionStorage.getItem(`${storageKey}-step`);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [filter, setFilter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(data));
  }, [data, storageKey]);

  useEffect(() => {
    sessionStorage.setItem(`${storageKey}-step`, String(stepIndex));
  }, [stepIndex, storageKey]);

  useEffect(() => {
    setFilter('');
  }, [stepIndex]);

  const steps: StepDef[] = useMemo(() => {
    const base: StepDef[] = [
      {
        id: 'client',
        title: 'Select client',
        disabledButtonlabel: 'Select the client',
        canContinue: (d: MeetingNoteFlowData) => !!d.client?.id,
        canFilter: true,
        render: (props: MeetingNoteStepProps) => (
          <NoteClientStep {...props} preselectClientId={currentClient?.id} />
        ),
      },
      {
        id: 'source',
        title: 'Select source',
        disabledButtonlabel: 'Select a source',
        canContinue: (d: MeetingNoteFlowData) => !!d.selectedRecordingId,
        render: (props: MeetingNoteStepProps) => <NoteSourceStep {...props} />,
      },
    ];

    if (flagEnabledMeetingNoteFromAgenda) {
      base.push({
        id: 'structure',
        title: 'Select structure',
        disabledButtonlabel: 'Select a structure',
        canContinue: (d: MeetingNoteFlowData) => !!d.templateType,
        render: (props: MeetingNoteStepProps) => <NoteStructureStep {...props} />,
      });
    }

    if (data.templateType === 'agenda') {
      base.push({
        id: 'template',
        title: 'Select agenda',
        disabledButtonlabel: 'Select an agenda',
        canContinue: (d: MeetingNoteFlowData) => !!d.selectedAgendaFileId,
        render: (props: MeetingNoteStepProps) => <NoteAgendaStep {...props} />,
      });
    } else {
      base.push({
        id: 'template',
        title: 'Select template',
        disabledButtonlabel: 'Select a template',
        canContinue: (d: MeetingNoteFlowData) => !!d.selectedTemplateId,
        render: (props: MeetingNoteStepProps) => <NoteTemplateStep {...props} />,
      });
    }

    base.push({
      id: 'processing',
      title: 'Processing',
      disabledButtonlabel: 'Processing...',
      canContinue: () => true,
      render: (props: MeetingNoteStepProps) => (
        <NoteProcessingStep {...props} onSubmittingChange={setIsSubmitting} />
      ),
    });

    return base;
  }, [flagEnabledMeetingNoteFromAgenda, data.templateType, currentClient?.id]);

  useEffect(() => {
    if (stepIndex >= steps.length) {
      setStepIndex(steps.length - 1);
    }
  }, [steps.length, stepIndex]);

  const current = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const canContinue = (current.canContinue ? current.canContinue(data) : true) && !data.isLoading;
  const canFilter = current.canFilter ?? false;

  const resetCurrentStepSelection = () => {
    setData(prev => {
      switch (current.id) {
        case 'source':
          return { ...prev, selectedRecordingId: undefined, selectedSourceType: undefined };
        case 'structure':
          return {
            ...prev,
            templateType: undefined,
            selectedTemplateId: undefined,
            selectedAgendaFileId: undefined,
          };
        case 'template':
          return { ...prev, selectedTemplateId: undefined, selectedAgendaFileId: undefined };
        default:
          return prev;
      }
    });
  };

  const goBack = () => {
    if (!isFirst) setStepIndex(i => i - 1);
    resetCurrentStepSelection();
  };

  const goNext = () => {
    if (!isLast) setStepIndex(i => i + 1);
  };

  const clearFlowStorage = () => {
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(`${storageKey}-step`);
  };

  const onClose = () => {
    clearFlowStorage();
    navigate(backHref);
  };

  const stepLabelMap: Partial<Record<MeetingNoteStepId, string>> = {
    template: 'Generate meeting note',
  };
  const continueLabel = !canContinue
    ? current.disabledButtonlabel
    : (stepLabelMap[current.id] ?? 'Continue');

  return (
    <>
      <Flex direction="column" h="100%" pos="relative">
        <FlowLayout
          header={
            !isLast ? (
              <FlowHeader onClose={onClose} onBack={stepIndex > 0 ? goBack : undefined} />
            ) : undefined
          }
          body={
            <FlowBody
              footerAlign="center"
              footer={
                <Flex direction="column" w={360}>
                  {canFilter && (
                    <SearchPill
                      value={filter}
                      onChange={setFilter}
                      placeholder="Search client by name/date"
                    />
                  )}

                  {current.id !== 'processing' && (
                    <PillButton
                      fullWidth
                      disabled={!canContinue}
                      label={continueLabel}
                      onClick={goNext}
                    />
                  )}
                </Flex>
              }
            >
              {current.render({ data, setData, onNext: goNext, filter })}
            </FlowBody>
          }
        />
      </Flex>
      <SidebarContent>
        {flagEnabledNewSuitabilitySiestaBar ? (
          <SiestaBarLayout>
            <MeetingNoteSidebar
              stepIndex={stepIndex}
              steps={steps}
              data={data}
              currentStepId={current.id}
              isSubmitting={isSubmitting}
            />
          </SiestaBarLayout>
        ) : (
          <Center h="100%">
            <Text size="xl" c="white">
              Step {stepIndex + 1} of {steps.length}: {current.title}
            </Text>
          </Center>
        )}
      </SidebarContent>
    </>
  );
}
